// @flow

import { toUnicode } from 'punycode.js';
import { createAction } from 'redux-act';

import { selectPricingInfos, type Currency } from '@gandi/billing';
import {
  getCart,
  selectRawProductsArray,
  addCartProducts,
  type Process,
  type ProductData,
  type ProductPayload,
  type ProductType,
} from '@gandi/cart';
import type { FetchResponse } from '@gandi/resource/redux';
import type { TranslatorProps } from '@gandi/translate';

import { GET, POST } from 'gandi.redux-fetcher';
import { createLogger } from 'gandi.redux-logger';

import { appendServicesBulkData } from 'src/DomainRenew/modules/domainServices';
import type { BulkProducts } from 'src/DomainRenew/types';
import type { Dispatch, ThunkAction } from 'src/types';

import type {
  RenewableProduct,
  ExtendedRenewableProduct,
  PriceApiResult,
  QuotedProduct,
} from '../types';

import { isRestorable, isTrial, isRenewable } from './helpers';

const logger = createLogger('renew/restore');

export const setRenewableProducts: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[Domain/Renew] Set renewable products');

const getProductDataForRenewableProduct =
  (product: RenewableProduct): (Dispatch => Promise<ProductData>) =>
  async dispatch => {
    let productData;
    switch (product.type) {
      case 'simple-hosting':
        productData = {
          identifier: product.id,
          payload: {
            duration: 12,
            duration_unit: 'm',

            // Not used by any API, only stored in the product payload for convenience.
            // Note that this will not be used in the cart widget.
            label: product.name,
            isTrial: isTrial(product),
          },
          process: 'renew',
          product_type: 'paas',
        };
        break;
      case 'domain':
      default:
        /* eslint-disable no-case-declarations */
        // fallback value in case proper retrieval of the suiting process fails.
        const probablyRestorable = isRestorable(product);
        let process: Process = probablyRestorable ? 'restore' : 'renew';

        // fallback value in case retrieving correct minimal duration fails.
        let duration = 1;

        productData = {
          process,
          product_type: 'domain',
          identifier: product.name,
          payload: {
            duration,
            domain_id: product.external_id,
          },
        };

        try {
          const res = await dispatch(
            GET(`/domain-anonymous/domains/${product.name}/renew`, {
              on200: (response: FetchResponse<>) => response,
              // $FlowFixMe[incompatible-call]
              not200: (response: FetchResponse<>) => Promise.reject(response),
            }),
          );
          // T54746
          const renew = res.data.renew || res.data;
          if (!isRenewable(renew)) {
            throw new Error('Domain product is not renewable.');
          }

          duration = (renew.durations || [])[0] || duration;
          process = 'renew';
        } catch (err) {
          try {
            // check if the domain is restorable
            const { data: restoreInfos } = await dispatch(
              GET(`/domain-anonymous/domains/${product.name}/restore`, {
                on200: (response: FetchResponse<>) => response,
                // $FlowFixMe[incompatible-call]
                not200: (response: FetchResponse<>) => Promise.reject(response),
              }),
            );
            if (restoreInfos.restorable) {
              duration = (restoreInfos.durations || [])[0] || duration;
              process = 'restore';
            }
          } catch (err2) {
            /* pass */
          }
        }

        productData.payload.duration = duration;
        productData.process = process;
        break;
    }

    return productData;
  };

export const addRenewableProductsToCart =
  (
    translator: TranslatorProps,
    products: Array<ExtendedRenewableProduct>,
  ): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const cartProductsIdentifiers = selectRawProductsArray(getState())
      .map(product => product.identifier)
      .filter(Boolean);

    const renewableProductsToAdd = products.filter(
      product =>
        !cartProductsIdentifiers.includes(product.name) &&
        !cartProductsIdentifiers.includes(product.id),
    );

    // grab product data
    const productDataPromises: Array<Promise<ProductData>> = [];

    renewableProductsToAdd.forEach(product => {
      productDataPromises.push(
        dispatch(getProductDataForRenewableProduct(product)),
      );
    });

    let productDataResults: Array<$SettledPromiseResult<ProductData>> = [];
    try {
      productDataResults = await Promise.allSettled(productDataPromises);
    } catch (e) {
      // fail silently
    }

    let bulkProducts: BulkProducts = productDataResults
      .map(result => result.value) // get the returned value of each promise
      .reduce(
        // create bulk data
        (
          acc: {
            data: Array<ProductData>,
            extra: BulkProducts['extra'],
          },
          current: void | ProductData,
        ) => {
          if (!current) return acc;
          acc.data.push(current);
          // XXX: current.identifier might be undefined according type
          // $FlowFixMe[incompatible-type] refine @gandi/cart ProductData
          acc.extra[current.identifier] = {
            // $FlowFixMe[incompatible-type]
            label: current.identifier,
            // $FlowFixMe[incompatible-type]
            identifier: current.identifier,
          };
          return acc;
        },
        { data: [], extra: {} },
      );

    bulkProducts = await dispatch(
      appendServicesBulkData(translator, bulkProducts),
    );

    try {
      await dispatch(
        addCartProducts({
          productsData: bulkProducts.data,
          extra: bulkProducts.extra,
        }),
      );
    } finally {
      await dispatch(getCart());
    }
  };

export const fetchRenewableProducts =
  (): (Dispatch => Promise<any>) => async dispatch => {
    const params = {
      sort_by: 'meta_data.expires_at',
      page: 1,
      meta_data: {
        autorenew: false,
        restorable: false,
      },
      per_page: 20,
      type: ['domain', 'simple-hosting'],
    };

    return dispatch(
      GET('/organization/search', {
        params,
        on200: async response => {
          // $FlowFixMe[incompatible-use]
          // $FlowFixMe[missing-local-annot]
          const filtered = response.data.filter(product => {
            const dateLimit = Date.now() + 1000 * 60 * 60 * 24 * 90;
            const dateExpiresAt = new Date(
              product.meta_data.expires_at,
            ).getTime();

            return dateExpiresAt < dateLimit;
          });

          let renewableProducts = [
            ...filtered.filter(product => product.type !== 'domain'),
          ];

          const domainProducts = filtered.filter(
            product => product.type === 'domain',
          );

          if (domainProducts.length) {
            const search = new URLSearchParams({
              lapse: 'false',
            });

            domainProducts.forEach(product =>
              search.append('fqdn', product.name),
            );

            await new Promise((resolve, reject) => {
              dispatch(
                GET(`/domain/domains_v2?${search.toString()}`, {
                  on200: res => {
                    renewableProducts = [
                      ...renewableProducts,
                      ...domainProducts.filter(d =>
                        // $FlowFixMe[incompatible-use]
                        // $FlowFixMe[missing-local-annot]
                        res.data.find(u => u.fqdn === d.name),
                      ),
                    ];
                    return resolve();
                  },
                  not200: res => {
                    dispatch(
                      logger.error(
                        { res },
                        'Cannot fetch domain_v2 products lapse status',
                      ),
                    );
                    return reject(res);
                  },
                }),
              );
            });
          }

          dispatch(setRenewableProducts(renewableProducts));
          return renewableProducts;
        },
        // $FlowFixMe[incompatible-call]
        not200: (response: FetchResponse<>) => {
          dispatch(
            logger.error({ res: response }, 'Cannot fetch renewable products'),
          );
          return Promise.reject(response);
        },
      }),
    );
  };

export const fetchQuotation =
  (body: {
    country?: string,
    currency?: string,
    discounts?: Array<string>,
    grid?: string,
    products: Array<{
      identifier?: string,
      payload?: ProductPayload,
      process: Process,
      reference?: number,
      type: ProductType,
    }>,
  }): (Dispatch => Promise<any>) =>
  dispatch =>
    dispatch(
      POST(`/quotations/`, body, {
        on200: (response: FetchResponse<>) => response,
        // $FlowFixMe[incompatible-call]
        not200: (response: FetchResponse<>) => response,
      }),
    ).then(res => {
      if (
        !res ||
        !res.data ||
        !Array.isArray(res.data.prices) ||
        !res.data.prices.length
      ) {
        const err = new Error('Cannot fetch quotation');
        // $FlowFixMe[incompatible-call]
        dispatch(logger.error(err));
        throw err;
      }
      return res.data;
    });

const cumulatePrice = (
  accPrice: number,
  price: number,
  taxRate: number,
): number =>
  (accPrice * 100 + price * 100 * (taxRate / 100) + price * 100) / 100;

export const reducePrices = (
  prices: Array<PriceApiResult>,
  identifier: string,
  currency: Currency,
  type: string,
): QuotedProduct =>
  prices.reduce<QuotedProduct>(
    (acc: QuotedProduct, quotation: PriceApiResult) => {
      // eslint-disable-next-line no-param-reassign
      quotation.description = toUnicode(quotation.description);
      // eslint-disable-next-line no-param-reassign
      quotation.comment = toUnicode(quotation.comment);
      if (
        quotation.description === identifier ||
        quotation.comment === identifier
      ) {
        acc.price = {
          beforeTaxes: cumulatePrice(
            acc.price?.beforeTaxes || 0,
            quotation.price_total_ht,
            0,
          ),
          afterTaxes: cumulatePrice(
            acc.price?.afterTaxes || 0,
            quotation.price_total_ht,
            quotation.tax_rate || 0,
          ),
        };

        if (
          (type === 'domain' &&
            quotation.product_type === type &&
            quotation.description === identifier) ||
          (type === 'paas' &&
            quotation.product_type === type &&
            quotation.comment === identifier &&
            quotation.description !== 'disk_renew')
        ) {
          acc.duration = quotation.duration;
          acc.duration_unit = quotation.duration_unit;
          acc.name = identifier;
        } else {
          acc.options = (acc.options || []).concat(
            quotation.product_type === 'domain_option'
              ? quotation.description
              : quotation.product_type,
          );
        }
      }

      return acc;
    },
    { currency, type },
  );

export type FetchProductQuotations<R> = (
  renewableProducts: Array<ExtendedRenewableProduct>,
) => R;
export type FetchProductQuotationsReturn = Promise<Array<QuotedProduct>>;
export type FetchProductQuotationsThunkAction = FetchProductQuotations<
  ThunkAction<FetchProductQuotationsReturn>,
>;
export const fetchProductQuotations: FetchProductQuotationsThunkAction =
  renewableProducts => async (dispatch, getState) => {
    const pricingInfos = selectPricingInfos(getState());

    const pDataRenew: Array<Promise<ProductData>> = renewableProducts.map(
      product => dispatch(getProductDataForRenewableProduct(product)),
    );
    const allSettledPromiseResults = await Promise.allSettled(pDataRenew);

    const products = allSettledPromiseResults
      .map(result => result.value || undefined)
      .filter(Boolean)
      .map(({ product_type: productType, ...productData }) => ({
        ...productData,
        type: productType,
        name: productData.payload?.label,
      }));

    const body = {
      ...pricingInfos,
      products,
    };

    const quotationsData: {
      currency: Currency,
      prices: Array<PriceApiResult>,
      // $FlowFixMe[prop-missing]
    } = await dispatch(fetchQuotation(body));

    const quotedProducts = products.map(product =>
      reducePrices(
        quotationsData.prices,
        product.name || product.identifier || '',
        quotationsData.currency,
        product.type,
      ),
    );

    return quotedProducts;
  };
